<script setup>
import {computed, provide, ref, nextTick, onMounted} from 'vue';
import {Form} from 'vee-validate';
import {useI18n} from 'vue-i18n';
import {useDebug} from '../../composables/useDebug';
import {useDccsForm} from './composables/useDccsForm';
import {useReCaptcha} from 'vue-recaptcha-v3'
import axios from '../../setup/axios';

const {log, error} = useDebug();
const {t} = useI18n();

const props = defineProps({
    action: {
        type: String,
        required: true,
        default: null,
    },

    method: {
        type: String,
        default: 'post',
        validator: value => {
            return ['get', 'post', 'patch', 'delete'].indexOf(value) !== -1;
        },
    },

    buttonLabel: {
        type: String,
        default: '',
    },

    submitLabel: {
        type: String,
        default: '',
    },

    redirectUrl: {
        type: String,
        default: ''
    },

    responseType: {
        type: String,
        default: 'json',
    },

    submitSuccessMsg: {
        type: String,
        default: '',
    },

    submitErrorMsg: {
        type: String,
        default: '',
    },

    hasDialog: {
        type: String,
        default: 'false',
    },

    prefersOptional: {
        type: Boolean,
        default: false,
    },

    requiredAppend: {
        type: String,
        default: '',
    },

    optionalAppend: {
        type: String,
        default: '(optional)',
    },

    formTitle: {
        type: String,
        default: '',
    },

    dataGtEventName: {
        type: String,
        default: 'formSent'
    }
});
const formRef = ref(null);
const merkurFormFieldsRef = ref(null);
const {
    busy,
    hasValidRedirectUrl,
    normalizedRedirectUrl,
    generateFormPayload,
    handleDownloadResponse
} = useDccsForm(props);
const {executeRecaptcha} = useReCaptcha();

const sentSuccessfully = ref(false);
const hasError = ref(false);
const errorMessage = ref(null);
const messageWithoutDialogRef = ref(null);
const hiddenElement = ref(null);
const dlLinkExists = ref(false);
const dlLinkClicked = ref(null);

const hasDialog = computed(() => props.hasDialog === 'true');


provide('busy', busy);
provide('prefersOptional', props.prefersOptional);
provide('requiredAppend', props.requiredAppend);
provide('optionalAppend', props.optionalAppend);


const scrollToMessageOrShowDialog = () => {
    if (hasDialog.value) {
        if (!document.body.classList.contains('active-form-submit')) {
            document.body.classList.add('active-form-submit');
        }
    } else if (messageWithoutDialogRef.value && hiddenElement.value) {
        hiddenElement.value?.scrollIntoView({behavior: "smooth"})
    }
}

const resetMessages = () => {
    if ((sentSuccessfully.value || hasError.value) && !hasDialog.value) {

        sentSuccessfully.value = false;
        hasError.value = false;
        errorMessage.value = null;
    }
};


const closeFormSubmit = () => {
    sentSuccessfully.value = false;
    hasError.value = false;
    errorMessage.value = null;

    if (document.body.classList.contains('active-form-submit')) {
        document.body.classList.remove('active-form-submit');
    }

};
const dlInfoTextRef = ref(null);

const sendTo = (rawFormData, {resetForm}) => {

    if (document.getElementById('dl-info-link')) {
        dlLinkExists.value = true;
    }

    if (dlLinkClicked.value === false) {
        dlInfoTextRef.value.classList.remove('is-hidden');
    }

    if (busy.value) {
        return;
    }

    busy.value = true;
    hasError.value = false;
    errorMessage.value = null;

    if (dlLinkClicked.value === false && dlLinkExists.value === true) {
        return;
    }

    return generateFormPayload(rawFormData)
        .then(payload => {
            log('Payload transformed successfully:', payload);

            executeRecaptcha('login')
                .then(token => {

                    payload['h-captcha-response'] = token;


                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({'formName': props.formTitle, 'event': props.dataGtEventName});


                    axios[props.method](props.action, payload, {responseType: props.responseType})
                        .then(response => {
                            log('Form submitted successfully:', response);

                            if (response.headers['content-type'] === 'application/octet-stream') {
                                handleDownloadResponse(response);
                            }

                            sentSuccessfully.value = true;
                            resetForm();

                            if (props.redirectUrl && props.redirectUrl.trim() !== '') {
                                const apiURL = "https://portal.merkur.at/partner/api/public/vermittler/publicinfos"
                                axios.get(apiURL, {responseType: 'json'})
                                    .then(response => {

                                        if (response.status === 200 && response.data && response.data.hardfacts) {
                                            const facts = response.data.hardfacts;

                                            if (props.redirectUrl.value.includes("?")) {
                                                props.redirectUrl.value += "&";
                                            } else {
                                                props.redirectUrl.value += "?";
                                            }

                                            if (facts.vermittlernummer !== undefined && facts.vermittlernummer !== null) {
                                                props.redirectUrl.value += 'agent=' + facts.vermittlernummer;
                                                if (facts.name !== undefined && facts.name !== null) {
                                                    props.redirectUrl.value += ',' + facts.name;
                                                }
                                            } else if (facts.name !== undefined && facts.name !== null) {
                                                props.redirectUrl.value += 'agent=' + facts.name;
                                            }
                                        }

                                        location.href = props.redirectUrl;
                                    })
                                    .catch(e => {
                                        location.href = props.redirectUrl;
                                        console.error(e);
                                    })
                            }
                        })
                        .catch(errorResponse => {
                            error(errorResponse);
                            hasError.value = true;

                            if (errorResponse.status === 403) {
                                errorMessage.value = t('form.vue_contactform_expired');
                                return;
                            }

                            errorMessage.value =
                                Object.prototype.hasOwnProperty.call(errorResponse, 'response') &&
                                Object.prototype.hasOwnProperty.call(errorResponse.response, 'data') &&
                                errorResponse.response.data &&
                                Object.prototype.hasOwnProperty.call(errorResponse.response.data, 'message')
                                    ? errorResponse.response.data.message
                                    : t('form.vue_contactform_error');
                        })
                        .finally(() => {
                            nextTick(() => {
                                busy.value = false;
                                scrollToMessageOrShowDialog();
                            });
                        });
                })
                .catch(errorResponse => {
                    error(errorResponse);
                    hasError.value = true;
                    errorMessage.value = errorResponse.response?.message || t('form.vue_contactform_error');

                    nextTick(() => {
                        busy.value = false;
                    });

                    if (!document.body.classList.contains('active-form-submit')) {
                        document.body.classList.add('active-form-submit');
                    }
                })
        });
};

const submitForm = (rawFormData, {resetForm}) => {

    if (busy.value !== false) {
        return;
    }

    busy.value = true;
    hasError.value = false;
    errorMessage.value = null;

    return generateFormPayload(rawFormData)
        .then(payload => {
            log('Payload transformed successfully:', payload);

            executeRecaptcha('login')
                .then(token => {

                    payload['h-captcha-response'] = token;

                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({'formName': props.formTitle, 'event': props.dataGtEventName});

                    axios[props.method](props.action, payload, {responseType: props.responseType})
                        .then(response => {
                            log('Form submitted successfully:', response);

                            if (response.headers['content-type'] === 'application/octet-stream') {
                                handleDownloadResponse(response);
                            }

                            sentSuccessfully.value = true;
                            resetForm();

                            log('Has valid redirect url ', hasValidRedirectUrl.value);
                            if (hasValidRedirectUrl.value) {
                                axios
                                    .head(normalizedRedirectUrl.value)
                                    .then(() => (window.location.href = normalizedRedirectUrl.value))
                                    .catch(error => console.error(error));
                            }
                        })
                        .catch(errorResponse => {
                            error(errorResponse);
                            hasError.value = true;

                            if (errorResponse.status === 403) {
                                errorMessage.value = t('form.error_sessionExpired');
                            } else if (errorResponse?.message?.length && errorResponse.message.includes('captcha invalid')) {
                                errorMessage.value = t('form.error_captchaValidationError');
                            } else if (errorResponse.status === 400) {
                                errorMessage.value = t('form.error_invalid');
                            } else {
                                errorMessage.value = t('form.error_unknown');
                            }

                        })
                        .finally(() => {
                            nextTick(() => {
                                busy.value = false;
                                scrollToMessageOrShowDialog();
                            });
                        });
                })
                .catch(e => {
                    error(e);
                    hasError.value = true;

                    if (error.status === 403) {
                        errorMessage.value = t('form.vue_contactform_expired');
                        return;
                    }

                    errorMessage.value =
                        Object.prototype.hasOwnProperty.call(error, 'response') &&
                        Object.prototype.hasOwnProperty.call(error.response, 'data') &&
                        error.response.data &&
                        Object.prototype.hasOwnProperty.call(error.response.data, 'message')
                            ? error.response.data.message
                            : t('form.vue_contactform_error');
                })
                .finally(() => {
                    busy.value = false;
                });
        })
        .catch(errorResponse => {
            error(errorResponse);
            hasError.value = true;
            errorMessage.value = errorResponse.response?.message || t('form.error.unknown');

            nextTick(() => {
                busy.value = false;
            });
        });
};
onMounted(() => {
    if (document.getElementById('dl-info-link')) {
        document.getElementById('dl-info-link').addEventListener("click", () => {
            dlLinkClicked.value = true;
        })
    }
});


</script>
<template>
    <Form ref="formRef" @input="resetMessages" v-slot="{ handleSubmit, isSubmitting }" as="div"
          :class="['merkur-form-container']" role="form">
        <div class="merkur-form-fields" ref="merkurFormFieldsRef">
            <slot v-for="(_, name) in $slots" :slot="name" :name="name" :busy="busy"/>
        </div>

        <div class="merkur-form-actions has-text-center">
            <template v-if="submitLabel === 'Weiter zum Onlineabschluss'">
                <p class="is-hidden" style="color: #e34a4a;" ref="dlInfoTextRef"
                   v-html="t('form.vue_contactform_dlInfoText')"/>

                <button
                    @click.prevent="event => handleSubmit(event, sendTo)"
                    class="button is-primary"
                    :disabled="busy"
                    :class="{ 'is-loading': busy }">
                <span class="icon">
                        <svg class="icon-chat" width="24px" height="24px">
                            <use xlink:href="#icon-chat"></use>
                        </svg>
                    </span>
                    <span>
                        {{ submitLabel }}
                    </span>

                </button>
            </template>
            <template v-else>
                <button
                    class="button is-primary"
                    :class="{ 'is-loading': busy }"
                    :disabled="busy"
                    @click.prevent="event => handleSubmit(event, submitForm)">
                <span class="icon">
                            <svg class="icon-chat" width="24px" height="24px">
                                <use xlink:href="#icon-chat"></use>
                            </svg>
                        </span>
                    <span>
                        {{ submitLabel }}
                    </span>

                </button>
            </template>
        </div>

        <div class="merkur-form-captcha">
            <p>
                <small v-html="t('form.vue_contactform_protected')"></small>
            </p>
        </div>
        <div v-if="!busy && (sentSuccessfully || hasError) && hasDialog " class="form-submit-message">
            <div class="close-form-submit icon" @click="closeFormSubmit">
                <svg width="37" height="37" viewBox="0 0 37 37">
                    <path
                        d="M21.4501 18.2677C21.3034 18.1214 21.3031 17.884 21.4494 17.7374C21.4496 17.7371 21.4498 17.7369 21.4501 17.7367L35.3446 3.84368C36.2231 2.96389 36.2221 1.53848 35.3423 0.65993C34.4625 -0.218617 33.0371 -0.217609 32.1586 0.66218L18.2656 14.5492C18.1193 14.6958 17.8819 14.6961 17.7352 14.5499C17.735 14.5496 17.7348 14.5494 17.7346 14.5492L3.84157 0.66218C2.96302 -0.216781 1.53827 -0.217117 0.659319 0.66143C-0.219642 1.53998 -0.219978 2.96473 0.658569 3.84368L14.5501 17.7367C14.6967 17.8829 14.697 18.1204 14.5507 18.267C14.5505 18.2672 14.5503 18.2675 14.5501 18.2677L0.658569 32.1622H0.658569C-0.219979 33.0411 -0.219643 34.4659 0.659319 35.3444C1.53828 36.223 2.96302 36.2226 3.84157 35.3437L17.7346 21.4492C17.8808 21.3025 18.1183 21.3022 18.2649 21.4485C18.2651 21.4487 18.2653 21.449 18.2656 21.4492L32.1586 35.3437C33.0371 36.2226 34.4619 36.223 35.3408 35.3444C36.2198 34.4659 36.2201 33.0411 35.3416 32.1622L21.4501 18.2677Z"
                        fill="white" fill-opacity="0.5"/>
                </svg>
            </div>
            <div class="form-submit-container">
                <div class="message">
                    <p v-if="sentSuccessfully" v-html="submitSuccessMsg"/>
                    <p v-if="hasError" v-html="errorMessage"/>
                    <button class="button is-primary" @click="closeFormSubmit"
                            v-html="t('form.vue_contactform_submit_close')"/>
                </div>
            </div>
        </div>
        <div v-else-if="!busy && (sentSuccessfully || hasError) && !hasDialog " class="text-center form-message"
             ref="messageWithoutDialogRef">
            <div class="hidden-element" ref="hiddenElement"/>
            <p v-if="sentSuccessfully" v-html="submitSuccessMsg"/>
            <p v-if="hasError" v-html="errorMessage"/>
        </div>
    </Form>
</template>
